import { httpClient } from './http';
import { API_ENDPOINTS } from '../config/api';
import { storageService } from './storage';

export interface DashboardStats {
  todaySales: number;
  todayOrders: number;
  activeTables: number;
  totalWaiters: number;
  averageOrderValue: number;
  peakHour: string;
  mostPopularItem: string;
  pendingOrders: number;
}

export interface Restaurant {
  id: string;
  name: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  name: string;
  icon?: string;
  color?: string;
  restaurantId?: string;
}

export interface MenuItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  categoryId: string;
  categoryName?: string;
  restaurantId: string;
  available: boolean;
  status: 'active' | 'inactive';
}

export interface Order {
  id: string;
  restaurantId: string;
  totalAmount: number;
  status: string;
  createdAt: string;
  created_at?: string;
}

export const dashboardService = {
  // Obtener estadísticas del dashboard desde el backend real
  getDashboardStats: async (userId: string, restaurantId: string): Promise<DashboardStats> => {
    try {
      const stored = await storageService.getSelectedRestaurant();
      const tz = stored?.timezone || null;
      const response = await httpClient.get<any>(
        API_ENDPOINTS.DASHBOARD_STATS(userId, restaurantId),
        tz ? { tz_name: tz.name, tz_offset: String(tz.offset) } : undefined,
        'authenticated'
      );
      // Backend devuelve { totalOrdersToday, totalTodaySales, timezone }
      const totalOrders = Number(response?.totalOrdersToday ?? 0);
      const totalRevenue = Number(response?.totalTodaySales ?? 0);
      // Si backend manda timezone actualizado, persistirlo
      if (response?.timezone && response.timezone.name) {
        await storageService.saveSelectedRestaurantTimezone({ name: response.timezone.name, offset: Number(response.timezone.offset ?? tz?.offset ?? 0) });
      }

      return {
        todaySales: totalRevenue,
        todayOrders: totalOrders,
        activeTables: 0,
        totalWaiters: 0,
        averageOrderValue: totalOrders > 0 ? totalRevenue / totalOrders : 0,
        peakHour: '—',
        mostPopularItem: '—',
        pendingOrders: 0,
      };
    } catch (error) {
      console.error('❌ Error obteniendo estadísticas del dashboard:', error);
      return {
        todaySales: 0,
        todayOrders: 0,
        activeTables: 0,
        totalWaiters: 0,
        averageOrderValue: 0,
        peakHour: '—',
        mostPopularItem: '—',
        pendingOrders: 0,
      };
    }
  },

  // Obtener restaurantes del usuario
  getUserRestaurants: async (userId: string): Promise<Restaurant[]> => {
    console.log('🏨 Obteniendo restaurantes del usuario:', userId);
    try {
      const response = await httpClient.get<Restaurant[]>(API_ENDPOINTS.RESTAURANTS(userId));
      const restaurants = (response as any).data || [];
      console.log('✅ Restaurantes obtenidos:', restaurants.length);
      return restaurants;
    } catch (error) {
      console.error('❌ Error obteniendo restaurantes:', error);
      return [];
    }
  },

  // Obtener categorías del restaurante
  getRestaurantCategories: async (userId: string, restaurantId: string): Promise<Category[]> => {
    console.log('📂 Obteniendo categorías del restaurante:', { userId, restaurantId });
    try {
      const response = await httpClient.get<Category[]>(API_ENDPOINTS.CATEGORIES_BY_RESTAURANT(userId, restaurantId));
      const categories = (response as any).data || [];
      console.log('✅ Categorías obtenidas:', categories.length);
      return categories;
    } catch (error) {
      console.error('❌ Error obteniendo categorías:', error);
      return [];
    }
  },

  // Obtener menú del restaurante
  getRestaurantMenu: async (restaurantId: string): Promise<MenuItem[]> => {
    console.log('🍽️ Obteniendo menú del restaurante:', restaurantId);
    try {
      const response = await httpClient.get<MenuItem[]>(`${API_ENDPOINTS.MENU}?restaurantId=${restaurantId}`);
      const menuItems = (response as any).data || [];
      console.log('✅ Items del menú obtenidos:', menuItems.length);
      return menuItems;
    } catch (error) {
      console.error('❌ Error obteniendo menú:', error);
      return [];
    }
  },

  // Obtener órdenes del restaurante
  getRestaurantOrders: async (userId: string, restaurantId: string): Promise<Order[]> => {
    console.log('📋 Obteniendo órdenes del restaurante:', { userId, restaurantId });
    try {
      const response = await httpClient.get<Order[]>(API_ENDPOINTS.ORDERS(userId, restaurantId));
      const orders = (response as any).data || [];
      console.log('✅ Órdenes obtenidas:', orders.length);
      return orders;
    } catch (error) {
      console.error('❌ Error obteniendo órdenes:', error);
      return [];
    }
  },

  // Crear nueva categoría
  createCategory: async (userId: string, categoryData: {
    name: string;
    icon?: string;
    color?: string;
    restaurantId: string;
  }): Promise<Category> => {
    console.log('➕ Creando nueva categoría:', categoryData);
    try {
      const response = await httpClient.post<Category>(API_ENDPOINTS.CATEGORIES(userId), categoryData);
      const category = (response as any).data;
      console.log('✅ Categoría creada:', category);
      return category;
    } catch (error) {
      console.error('❌ Error creando categoría:', error);
      throw new Error('Error al crear la categoría');
    }
  },

  // Crear nuevo item del menú
  createMenuItem: async (userId: string, menuItemData: {
    name: string;
    description?: string;
    price: number;
    categoryId: string;
    restaurantId: string;
    available?: boolean;
  }): Promise<MenuItem> => {
    console.log('➕ Creando nuevo item del menú:', menuItemData);
    try {
      const response = await httpClient.post<MenuItem>(API_ENDPOINTS.MENU_ADMIN(userId), menuItemData);
      const menuItem = (response as any).data;
      console.log('✅ Item del menú creado:', menuItem);
      return menuItem;
    } catch (error) {
      console.error('❌ Error creando item del menú:', error);
      throw new Error('Error al crear el item del menú');
    }
  },

  // Actualizar disponibilidad de item del menú
  updateMenuItemAvailability: async (userId: string, menuId: string, available: boolean): Promise<MenuItem> => {
    console.log('🔄 Actualizando disponibilidad del item:', { userId, menuId, available });
    try {
      const response = await httpClient.put<MenuItem>(API_ENDPOINTS.MENU_AVAILABILITY(userId, menuId), { available });
      const menuItem = (response as any).data;
      console.log('✅ Disponibilidad actualizada:', menuItem);
      return menuItem;
    } catch (error) {
      console.error('❌ Error actualizando disponibilidad:', error);
      throw new Error('Error al actualizar la disponibilidad');
    }
  },

  // Actualizar estado de orden
  updateOrderStatus: async (userId: string, restaurantId: string, orderId: string, status: string): Promise<Order> => {
    console.log('🔄 Actualizando estado de orden:', { userId, restaurantId, orderId, status });
    try {
      const response = await httpClient.put<Order>(API_ENDPOINTS.ORDER_STATUS(userId, restaurantId, orderId), { status });
      const order = (response as any).data;
      console.log('✅ Estado de orden actualizado:', order);
      return order;
    } catch (error) {
      console.error('❌ Error actualizando estado de orden:', error);
      throw new Error('Error al actualizar el estado de la orden');
    }
  },
}; 